import React, { useEffect, useState } from 'react';
import {
	Alert,
	AlertTitle,
	Box,
	CircularProgress,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	TextField,
} from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Button } from '../../../../../components/ui/button';
import { useDebouncedValue } from 'core/hooks/useDecbouncedValue';
import {
	formatToastRestaurantAddress,
	searchToastRestaurants,
	ToastRestaurant,
} from 'core/API/partners';
import styles from './CreatePartnerSettings.module.scss';

const TOAST_CONNECT_URL = 'https://auth.sandbox.eng.toasttab.com/u/login/';

export type ToastFlowStep = 'connect' | 'search' | 'confirm' | 'connected';

export interface ToastPartnerParameters {
	restaurant_guid: string;
	restaurant_name: string;
	location_name: string;
	restaurant_address: string;
	management_group_guid?: string;
	restaurant_city?: string;
	restaurant_state?: string;
	restaurant_zip_code?: string;
	restaurant_timezone?: string;
}

interface ToastConnectFlowProps {
	onParametersChange: (params: ToastPartnerParameters | null) => void;
	onReset?: () => void;
}

/**
 * Multi-step Toast POS onboarding:
 * connect instructions → restaurant search → confirm location → connected.
 */
const ToastConnectFlow: React.FC<ToastConnectFlowProps> = ({ onParametersChange, onReset }) => {
	const [step, setStep] = useState<ToastFlowStep>('connect');
	const [searchInput, setSearchInput] = useState('');
	const debouncedSearch = useDebouncedValue(searchInput, 400);
	const [results, setResults] = useState<ToastRestaurant[]>([]);
	const [isSearching, setIsSearching] = useState(false);
	const [searchError, setSearchError] = useState<string | null>(null);
	const [hasSearched, setHasSearched] = useState(false);
	const [selectedRestaurant, setSelectedRestaurant] = useState<ToastRestaurant | null>(null);

	useEffect(() => {
		const query = debouncedSearch.trim();
		if (step !== 'search' || query.length < 2) {
			if (query.length < 2) {
				setResults([]);
				setHasSearched(false);
				setSearchError(null);
			}
			return;
		}

		let cancelled = false;

		const runSearch = async () => {
			setIsSearching(true);
			setSearchError(null);
			try {
				const restaurants = await searchToastRestaurants(query);
				if (!cancelled) {
					setResults(restaurants);
					setHasSearched(true);
				}
			} catch (error: any) {
				if (!cancelled) {
					setResults([]);
					setHasSearched(true);
					setSearchError(error?.message || 'Failed to search Toast restaurants');
				}
			} finally {
				if (!cancelled) {
					setIsSearching(false);
				}
			}
		};

		runSearch();
		return () => {
			cancelled = true;
		};
	}, [debouncedSearch, step]);

	const buildParameters = (restaurant: ToastRestaurant): ToastPartnerParameters => ({
		restaurant_guid: restaurant.restaurant_guid,
		restaurant_name: restaurant.restaurant_name,
		location_name: restaurant.location_name,
		restaurant_address: formatToastRestaurantAddress(restaurant),
		management_group_guid: restaurant.management_group_guid,
		restaurant_city: restaurant.restaurant_city,
		restaurant_state: restaurant.restaurant_state,
		restaurant_zip_code: restaurant.restaurant_zip_code,
		restaurant_timezone: restaurant.restaurant_timezone,
	});

	const handleConnectToToast = () => {
		window.open(TOAST_CONNECT_URL, 'toast-connect', 'width=600,height=700,scrollbars=yes');
	};

	const goToSearch = () => {
		setStep('search');
		setSelectedRestaurant(null);
		onParametersChange(null);
	};

	const handleSelectRestaurant = (restaurant: ToastRestaurant) => {
		setSelectedRestaurant(restaurant);
		setStep('confirm');
		onParametersChange(null);
	};

	const handleConfirm = () => {
		if (!selectedRestaurant) return;
		onParametersChange(buildParameters(selectedRestaurant));
		setStep('connected');
	};

	const handleStartOver = () => {
		setStep('connect');
		setSearchInput('');
		setResults([]);
		setHasSearched(false);
		setSearchError(null);
		setSelectedRestaurant(null);
		onParametersChange(null);
		onReset?.();
	};

	const handleBackToSearch = () => {
		setStep('search');
		setSelectedRestaurant(null);
		onParametersChange(null);
	};

	return (
		<Box
			sx={{
				marginY: 2,
				padding: 2,
				border: '1px solid rgba(0, 0, 0, 0.12)',
				borderRadius: 1,
				backgroundColor:
					step === 'connected' ? 'rgba(76, 175, 80, 0.05)' : 'rgba(0, 0, 0, 0.02)',
			}}
		>
			<Box
				sx={{
					fontWeight: 400,
					fontSize: '16px',
					color: 'rgb(0 0 0 / 87%)',
					marginBottom: 1,
				}}
			>
				Toast Integration
			</Box>

			{step === 'connect' && (
				<>
					<Box sx={{ fontSize: '15px', fontWeight: 500, marginBottom: 1 }}>
						Connect your Toast account
					</Box>
					<Box sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)', marginBottom: 1.5 }}>
						To connect BYOD.AI:
					</Box>
					<Box
						component="ol"
						sx={{
							fontSize: '14px',
							color: 'rgba(0, 0, 0, 0.7)',
							margin: '0 0 16px 0',
							paddingLeft: 2.5,
							'& li': { marginBottom: 0.5 },
						}}
					>
						<li>Log in to your Toast account.</li>
						<li>Go to Integrations.</li>
						<li>Select Browse Integrations.</li>
						<li>Search for BYOD.AI.</li>
						<li>Add BYOD.AI to the required restaurant/location.</li>
					</Box>

					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
						<Button
							onClick={handleConnectToToast}
							className={styles.googleDriveButton}
							style={{
								backgroundColor: '#446388',
								color: '#fff',
								padding: '10px 20px',
								fontSize: '14px',
							}}
						>
							Connect to Toast
						</Button>
						<Button
							onClick={goToSearch}
							className={styles.reconnectButton}
							style={{
								color: '#446388',
								border: '1px solid #446388',
								backgroundColor: 'transparent',
								padding: '8px 16px',
								fontSize: '14px',
							}}
						>
							Continue to search
						</Button>
					</Box>

					<Box sx={{ fontSize: '13px', color: 'rgba(0, 0, 0, 0.6)', marginTop: 2 }}>
						Already connected BYOD.AI for this location?{' '}
						<Box
							component="button"
							type="button"
							onClick={goToSearch}
							sx={{
								border: 'none',
								background: 'none',
								padding: 0,
								color: '#446388',
								textDecoration: 'underline',
								cursor: 'pointer',
								fontSize: 'inherit',
								fontFamily: 'inherit',
							}}
						>
							Search your restaurant instead
						</Box>
					</Box>
				</>
			)}

			{step === 'search' && (
				<>
					<Box sx={{ fontSize: '15px', fontWeight: 500, marginBottom: 1 }}>
						Find your restaurant
					</Box>
					<Box sx={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)', marginBottom: 2 }}>
						Search for your restaurant by name. Select a specific location — the same
						restaurant may have multiple Toast locations.
					</Box>

					<TextField
						label="Type your restaurant name"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						margin="normal"
						fullWidth
						autoFocus
						InputProps={{
							endAdornment: isSearching ? (
								<CircularProgress color="inherit" size={20} />
							) : null,
						}}
						helperText="Enter at least 2 characters to search"
					/>

					{searchError && (
						<Alert severity="error" sx={{ marginTop: 1 }}>
							{searchError}
						</Alert>
					)}

					{hasSearched && !isSearching && !searchError && results.length === 0 && (
						<Alert severity="info" sx={{ marginTop: 1 }}>
							No restaurants found. Try a different name, or make sure BYOD.AI is added
							for this location in Toast.
						</Alert>
					)}

					{results.length > 0 && (
						<List
							dense
							sx={{
								marginTop: 1,
								border: '1px solid rgba(0, 0, 0, 0.12)',
								borderRadius: 1,
								maxHeight: 320,
								overflow: 'auto',
								backgroundColor: '#fff',
							}}
						>
							{results.map((restaurant) => {
								const address = formatToastRestaurantAddress(restaurant);
								const key = restaurant.restaurant_guid || String(restaurant.id);
								const hasLocation = Boolean(restaurant.location_name?.trim());
								return (
									<ListItemButton
										key={key}
										disabled={!hasLocation || !restaurant.restaurant_guid}
										onClick={() => handleSelectRestaurant(restaurant)}
										sx={{
											alignItems: 'flex-start',
											'&:hover': {
												backgroundColor: 'rgba(68, 99, 136, 0.08)',
											},
										}}
									>
										<ListItemIcon sx={{ minWidth: 36, marginTop: 0.5 }}>
											<LocationOnOutlinedIcon
												fontSize="small"
												sx={{ color: '#446388' }}
											/>
										</ListItemIcon>
										<ListItemText
											primary={restaurant.restaurant_name}
											secondary={
												<>
													{hasLocation
														? `📍 ${restaurant.location_name}`
														: '📍 Location required — cannot select'}
													{address ? (
														<>
															<br />
															{address}
														</>
													) : null}
												</>
											}
											primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
											secondaryTypographyProps={{ fontSize: 12, component: 'div' }}
										/>
									</ListItemButton>
								);
							})}
						</List>
					)}

					<Box sx={{ marginTop: 2 }}>
						<Button
							onClick={handleStartOver}
							className={styles.reconnectButton}
							style={{
								color: '#446388',
								border: '1px solid #446388',
								backgroundColor: 'transparent',
								padding: '8px 16px',
								fontSize: '14px',
							}}
						>
							Back
						</Button>
					</Box>
				</>
			)}

			{step === 'confirm' && selectedRestaurant && (
				<>
					<Box sx={{ fontSize: '15px', fontWeight: 500, marginBottom: 1 }}>
						Confirm your restaurant
					</Box>
					<Box
						sx={{
							fontSize: '14px',
							color: 'rgba(0, 0, 0, 0.75)',
							marginBottom: 2,
							lineHeight: 1.7,
						}}
					>
						<div>
							<strong>Restaurant:</strong> {selectedRestaurant.restaurant_name}
						</div>
						<div>
							<strong>Location:</strong>{' '}
							{selectedRestaurant.location_name || '—'}
						</div>
						<div>
							<strong>Address:</strong>{' '}
							{formatToastRestaurantAddress(selectedRestaurant) || '—'}
						</div>
					</Box>

					{!selectedRestaurant.location_name && (
						<Alert severity="warning" sx={{ marginBottom: 2 }}>
							This result is missing a location name. Please go back and select a
							specific Toast location.
						</Alert>
					)}

					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
						<Button
							onClick={handleConfirm}
							disabled={!selectedRestaurant.location_name || !selectedRestaurant.restaurant_guid}
							className={styles.googleDriveButton}
							style={{
								backgroundColor: '#446388',
								color: '#fff',
								padding: '10px 20px',
								fontSize: '14px',
								opacity:
									!selectedRestaurant.location_name || !selectedRestaurant.restaurant_guid
										? 0.6
										: 1,
							}}
						>
							Confirm &amp; Connect
						</Button>
						<Button
							onClick={handleBackToSearch}
							className={styles.reconnectButton}
							style={{
								color: '#446388',
								border: '1px solid #446388',
								backgroundColor: 'transparent',
								padding: '8px 16px',
								fontSize: '14px',
							}}
						>
							Back to search
						</Button>
					</Box>
				</>
			)}

			{step === 'connected' && selectedRestaurant && (
				<>
					<Alert severity="success" sx={{ marginBottom: 2 }}>
						<AlertTitle>Toast Connected ✓</AlertTitle>
						{selectedRestaurant.restaurant_name}
						{selectedRestaurant.location_name
							? ` — ${selectedRestaurant.location_name}`
							: ''}{' '}
						has been successfully connected to this Mabel workplace. Save the partner to
						finish.
					</Alert>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							fontSize: '14px',
							color: 'rgba(0, 0, 0, 0.7)',
							marginBottom: 2,
						}}
					>
						<CheckCircleOutlineIcon fontSize="small" sx={{ color: '#4caf50' }} />
						Connected — restaurant GUID: {selectedRestaurant.restaurant_guid}
					</Box>

					<Button
						onClick={handleStartOver}
						className={styles.reconnectButton}
						style={{
							color: '#446388',
							border: '1px solid #446388',
							backgroundColor: 'transparent',
							padding: '8px 16px',
							fontSize: '14px',
						}}
					>
						Change restaurant
					</Button>
				</>
			)}
		</Box>
	);
};

export default ToastConnectFlow;
